import { useMutation, useQueryClient } from "@tanstack/react-query"
import api from "@/lib/axios"
import FormHeader from "@/components/FormHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useState } from "react"
import { FaPlus } from "react-icons/fa6"
import { Loader2 } from "lucide-react"
import { JenjangPendidikan } from "@/types"
import { MdModeEdit } from "react-icons/md"
import toast from "react-hot-toast"
import { AxiosError } from "axios"
import { Switch } from "@/components/ui/switch"

const postData = async (newData: JenjangPendidikan) => {
  const response = await api.post("/master/jenjang-pendidikan/store", newData)
  return response.data
}

const patchData = async (newData: JenjangPendidikan) => {
  const response = await api.patch("/master/jenjang-pendidikan/update", newData)
  return response.data
}

export default function Form({
  method,
  selectedRow,
  closeModal,
}: {
  method: "POST" | "PATCH" | "DELETE"
  selectedRow?: JenjangPendidikan | null
  closeModal: () => void
}) {
  const [form, setForm] = useState({
    id_ref_jenjang_pendidikan: selectedRow?.id_ref_jenjang_pendidikan || 0,
    id_feeder: selectedRow?.id_feeder || 0,
    nama_jenjang_pendidikan: selectedRow?.nama_jenjang_pendidikan || "",
    skor: selectedRow?.skor || 0,
    is_status_tampil: false,
  })

  const isPost = method === "POST"
  const isPatch = method === "PATCH"

  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : async () => {},
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listJenjangPendidikan"] })
      closeModal()
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    mutate(form)
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={
          isPost
            ? "Insert Data Jenjang Pendidikan"
            : "Update Data Jenjang Pendidikan"
        }
      />
      <div>
        <Label>Nama Jenjang Pendidikan</Label>
        <Input readOnly disabled value={form.nama_jenjang_pendidikan} />
      </div>

      <div>
        <Label>Skor</Label>
        <Input
          type="number"
          placeholder="Ketik skor..."
          value={form.skor}
          onChange={(event) =>
            setForm((prev) => ({ ...prev, skor: Number(event.target.value) }))
          }
        />
      </div>

      <div className="flex gap-3">
        <Switch
          id="status"
          checked={form.is_status_tampil}
          onCheckedChange={() =>
            setForm((prev) => ({
              ...prev,
              is_status_tampil: !prev.is_status_tampil,
            }))
          }
        />
        <Label htmlFor="status">Status</Label>
      </div>

      <Button className="mt-3 flex items-center gap-3" disabled={isPending}>
        {isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            Processing...
          </>
        ) : (
          "Simpan"
        )}
      </Button>
    </form>
  )
}
